import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import {
    Alert,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Slide,
    Snackbar,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import * as React from 'react';
import { useEffect, useState, useContext } from "react";
import { AuthContext } from '../../../contexts/auth';
import { createArt, deleteArt, getArtId, getArts, getFontId, getFonts, logSearch, updateArtId } from "../../../services/api";
import { hasPermission, CONTEXTS, SCOPES } from '../../../services/permissions';
import { ArtsList } from "./ArtsList";
import { FilterBy } from "./FilterBy";

const LIMIT = 10;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export const SearchArtwork = () => {
    const { role } = useContext(AuthContext);
    const [fonts, setFonts] = useState([]);
    const [arts, setArts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [isFetchingData, setIsFetchingData] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [activeFilters, setActiveFilters] = useState({ search: '', author: '', font: null, fontId: null });
    const [paginationActive, setPaginationActive] = useState(true);

    // CRUD state from Arts.jsx
    const [open, setOpen] = useState(false);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [currentDeleteId, setCurrentDeleteId] = useState("");
    const [errors, setErrors] = useState({});
    const [currentId, setCurrentId] = useState("");
    const [isUpdate, setIsUpdate] = useState(false);
    const [code, setCode] = useState("");
    const [title, setTitle] = useState("");
    const [font, setFont] = useState("");
    const [selectedFontAttr, setSelectedFontAttr] = useState([]);
    const [addedSecondaryAttrs, setAddedSecondaryAttrs] = useState([]);
    const [form, setForm] = useState({});
    const [show, setShow] = useState(false);
    const [message, setMessage] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const showSnackbar = (msg, severity = 'success') => {
        setSnackbar({ open: true, message: msg, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    useEffect(() => {
        document.title = 'CEDOMCA | Pesquisar obras';
        getFonts()
            .then((res) => setFonts(res.data))
            .catch(() => setIsFetchingData(false));
    }, []);

    useEffect(() => {
        setIsFetchingData(false);
        fetchArts(null, '', '', 1);
    }, [fonts]);

    const fetchArts = (font, search, author, pg) => {
        getArts({ font, search: search || undefined, author: author || undefined, page: pg, limit: LIMIT })
            .then((res) => {
                setArts(res.data.data);
                setTotal(res.data.total);
                setPaginationActive(true);
            });
    };

    // CRUD logic from Arts.jsx
    const handleClose = () => {
        setCode("");
        setTitle("");
        setFont("");
        setErrors({});
        setForm({});
        setSelectedFontAttr([]);
        setAddedSecondaryAttrs([]);
        setOpen(false);
        setIsUpdate(false);
    };

    const setField = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    };

    const verifyErrorCode = (value) => {
        errors.code = null;
        setCode(value);
    };

    const verifyTitleCode = (value) => {
        errors.title = null;
        setTitle(value);
    };

    const fetchFontId = async (id) => {
        try {
            const { data } = await getFontId(id);
            setSelectedFontAttr(data.attributes);
            setAddedSecondaryAttrs([]);
            data.attributes.forEach(attr => setField(attr.name, ""));
        } catch (err) {
            console.error(err);
        }
    };

    const removeSecondaryAttr = (name) => {
        setAddedSecondaryAttrs(prev => prev.filter(n => n !== name));
        setErrors(prev => ({ ...prev, [name]: null }));
    };

    const setFontandSelectedFont = (value, id) => {
        errors.font = null;
        setFont(value);
        fetchFontId(id);
    };

    const findFormErrors = () => {
        const newErrors = {};

        if (!code || code === '') newErrors.code = 'Código obrigatório';
        if (!title || title === '') newErrors.title = 'Título obrigatório';
        if (!font || font === '') newErrors.font = 'Ficha catalográfica obrigatória';

        if (isUpdate) {
            selectedFontAttr.map(attrMapped => {
                if (!attrMapped.value || attrMapped.value === '') {
                    newErrors[attrMapped.name] = `${attrMapped.name.charAt(0).toUpperCase() + attrMapped.name.slice(1)} obrigatório`;
                }
            });
        } else {
            const hasPrimary = selectedFontAttr.some(a => a.isPrimary);
            const visibleAttrNames = hasPrimary
                ? [...selectedFontAttr.filter(a => a.isPrimary).map(a => a.name), ...addedSecondaryAttrs]
                : selectedFontAttr.map(a => a.name);
            visibleAttrNames.forEach(name => {
                if (!form[name] || form[name] === '') {
                    newErrors[name] = `${name.charAt(0).toUpperCase() + name.slice(1)} obrigatório`;
                }
            });
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = findFormErrors();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
        } else {
            const hasPrimary = selectedFontAttr.some(a => a.isPrimary);
            const visibleAttrNames = hasPrimary
                ? [...selectedFontAttr.filter(a => a.isPrimary).map(a => a.name), ...addedSecondaryAttrs]
                : selectedFontAttr.map(a => a.name);
            const attr = visibleAttrNames.map(name => ({ name, value: form[name] || '' }));

            const data = {
                code: code,
                title: title,
                font: font,
                attributes: attr
            };

            try {
                await createArt(data);
                handleClose();
                fetchArts(activeFilters.font, activeFilters.search, activeFilters.author, page);
                showSnackbar('Obra cadastrada com sucesso!');
            } catch (err) {
                setShow(true);
                let errorMsg = err.response?.data?.message?.toString() || "Erro ao criar arte.";
                let newErrorMsg = errorMsg.replaceAll(',', '\n\n');
                setMessage(newErrorMsg);
                setTimeout(() => {
                    setShow(false);
                    setMessage("");
                }, 7000);
            }
        }
    };

    const fetchArtId = async (id) => {
        try {
            const { data } = await getArtId(id);
            setCode(data.code);
            setTitle(data.title);
            setFont(data.font);
            setSelectedFontAttr(data.attributes);
            setCurrentId(id);
        } catch (err) {
            console.error(err);
        }
    };

    const handleUpdateArt = (id) => {
        fetchArtId(id);
        setIsUpdate(true);
        setOpen(true);
    };

    const handleSubmitUpdate = async (e, id) => {
        e.preventDefault();

        const newErrors = findFormErrors();
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
        } else {
            const attr = [];
            selectedFontAttr.map(attrMapped => {
                attr.push({
                    name: attrMapped.name,
                    value: attrMapped.value
                });
            });

            const data = {
                code: code,
                title: title,
                font: font,
                attributes: attr
            };

            try {
                await updateArtId(id, data);
                handleClose();
                fetchArts(activeFilters.font, activeFilters.search, activeFilters.author, page);
                showSnackbar('Obra atualizada com sucesso!');
            } catch (err) {
                setShow(true);
                let errorMsg = err.response?.data?.message?.toString() || "Erro ao atualizar arte.";
                let newErrorMsg = errorMsg.replaceAll(',', '\n\n');
                setMessage(newErrorMsg);
                setTimeout(() => {
                    setShow(false);
                    setMessage("");
                }, 7000);
            }
        }
    };

    const showConfirmDelete = (id) => {
        setCurrentDeleteId(id);
        setOpenConfirm(true);
    };

    const handleCloseConfirm = () => {
        setOpenConfirm(false);
        setCurrentDeleteId("");
    };

    const handleDeleteArt = async () => {
        setOpenConfirm(false);
        try {
            await deleteArt(currentDeleteId);
            setCurrentDeleteId("");
            fetchArts(activeFilters.font, activeFilters.search, activeFilters.author, page);
            showSnackbar('Obra excluída com sucesso!');
        } catch (err) {
            console.error("Erro ao excluir arte", err);
            showSnackbar('Erro ao excluir obra.', 'error');
        }
    };

    const updateSelectedFontAttr = (name, value) => {
        if (errors[name]) setErrors({ ...errors, [name]: null });

        return selectedFontAttr.map(attr => {
            if (attr.name === name) {
                return { ...attr, value: value };
            }
            return attr;
        });
    };

    const handleSearch = (search, author, font, fontId) => {
        setActiveFilters({ search, author, font, fontId });
        setPage(1);
        fetchArts(font, search, author, 1);
        logSearch({ query: search || null, author: author || null, font_id: fontId || null }).catch(() => {});
    };

    const handleClearFilters = () => {
        setActiveFilters({ search: '', author: '', font: null });
        setPage(1);
        fetchArts(null, '', '', 1);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchArts(activeFilters.font, activeFilters.search, activeFilters.author, newPage);
    };

    return (
        <Box sx={{ backgroundColor: '#f0f0f0', minHeight: '100vh', padding: '1.5rem' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant='h5' sx={{ fontWeight: 'bold' }}>
                    Lista de Obras
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {editMode && (
                        <Button
                            startIcon={<AddIcon />}
                            variant="contained"
                            onClick={() => setOpen(true)}
                            sx={{
                                backgroundColor: '#7BAAA7',
                                '&:hover': {
                                    backgroundColor: '#6A9391',
                                },
                                color: '#000000',
                                textTransform: 'none',
                                fontWeight: 600
                            }}
                        >
                            Adicionar nova obra
                        </Button>
                    )}
                    {hasPermission(role, CONTEXTS.art, SCOPES.canEdit) && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editMode}
                                    onChange={() => setEditMode(!editMode)}
                                    sx={{
                                        '& .MuiSwitch-track': { borderRadius: 12, height: 18 },
                                        '& .MuiSwitch-thumb': { width: 14, height: 14 },
                                        '& .MuiSwitch-switchBase': { padding: '14px' },
                                        '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(16px)' },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4a9e8e', opacity: 1 },
                                        '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb': { color: '#fff' },
                                    }}
                                />
                            }
                            label='Modo de Edição'
                            labelPlacement='start'
                        />
                    )}
                </Box>
            </Box>
            {!isFetchingData && (
                <Grid container spacing={3}>
                    <FilterBy
                        fonts={fonts}
                        onSearch={handleSearch}
                        onClearFilters={handleClearFilters}
                    />
                    <ArtsList
                        arts={arts}
                        total={total}
                        page={page}
                        limit={LIMIT}
                        paginationActive={paginationActive}
                        onPageChange={handlePageChange}
                        editMode={editMode}
                        onEdit={handleUpdateArt}
                        onDelete={showConfirmDelete}
                    />
                </Grid>
            )}

            <Dialog
                open={open}
                fullWidth
                maxWidth={'lg'}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleClose}
            >
                <DialogTitle>
                    <Grid container direction="row" justifyContent="space-between" alignItems="center">
                        <Button
                            onClick={handleClose}
                            sx={{ color: '#7BAAA7' }}
                        >
                            <KeyboardArrowLeft />
                            Voltar
                        </Button>
                        {isUpdate ? "Editar obra" : "Cadastrar obra"}
                        {isUpdate ? (
                            <Button variant="contained" onClick={event => handleSubmitUpdate(event, currentId)}
                                sx={{ backgroundColor: '#7BAAA7', '&:hover': { backgroundColor: '#6A9391' }, color: '#000' }}
                            >
                                Salvar
                            </Button>
                        ) : (
                            <Button variant="contained" onClick={handleSubmit}
                                sx={{ backgroundColor: '#7BAAA7', '&:hover': { backgroundColor: '#6A9391' }, color: '#000' }}
                            >
                                Cadastrar
                            </Button>
                        )}
                    </Grid>
                </DialogTitle>
                <DialogContent>
                    <Box
                        noValidate
                        component="form"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            m: 'auto',
                            width: 'auto',
                        }}
                    >
                        <Collapse in={show} sx={{ mb: 2, mt: 2 }}>
                            <Alert severity="error">
                                <p>{message}</p>
                            </Alert>
                        </Collapse>

                        <Grid
                            container
                            direction="row"
                            justifyContent="space-around"
                            alignItems="flex-start"
                            columns={12}
                            sx={{ mt: 2 }}
                        >
                            <Grid item xs={6} sx={{ px: 2 }}>
                                <TextField
                                    name="code"
                                    required
                                    fullWidth
                                    id="code"
                                    label="Código"
                                    sx={{ mt: 2 }}
                                    value={code}
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => verifyErrorCode(e.target.value)}
                                    {...(errors.code && {
                                        error: true,
                                        helperText: errors.code,
                                    })}
                                />

                                <TextField
                                    required
                                    fullWidth
                                    id="title"
                                    label="Título"
                                    name="title"
                                    sx={{ mt: 2 }}
                                    value={title}
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => verifyTitleCode(e.target.value)}
                                    {...(errors.title && {
                                        error: true,
                                        helperText: errors.title,
                                    })}
                                />

                                <FormControl sx={{ mt: 2, width: '100%' }}
                                    {...(errors.font && {
                                        error: true,
                                    })}>
                                    <InputLabel id="demo-simple-select-label">Ficha catalográfica</InputLabel>
                                    <Select
                                        disabled={isUpdate}
                                        fullWidth
                                        labelId="demo-simple-select-outlined-label"
                                        id="demo-simple-select-outlined"
                                        label="Possíveis Valores"
                                        name="font"
                                        value={font ?? ""}
                                        onChange={(e, child) => {
                                            const childKey = child.props['a-key'];
                                            setFontandSelectedFont(e.target.value, childKey);
                                        }}
                                    >
                                        {fonts.map(f => (
                                            <MenuItem a-key={f.id} key={f.id} value={f.name}>{f.name}</MenuItem>
                                        ))}
                                    </Select>
                                    {errors.font && <Typography variant="caption" color="error">{errors.font}</Typography>}
                                </FormControl>
                            </Grid>

                            <Grid item xs={6} sx={{ px: 2 }}>
                                <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
                                    {isUpdate ? (
                                        selectedFontAttr.map(attr => (
                                            <TextField
                                                key={attr.name}
                                                name={attr.name}
                                                required
                                                id={attr.name}
                                                label={attr.name}
                                                value={attr.value}
                                                sx={{ mt: 2, width: '48%' }}
                                                onChange={(e) => {
                                                    const updatedAttr = updateSelectedFontAttr(attr.name, e.target.value);
                                                    setSelectedFontAttr(updatedAttr);
                                                }}
                                                {...(errors[attr.name] && {
                                                    error: true,
                                                    helperText: errors[attr.name],
                                                })}
                                            />
                                        ))
                                    ) : (() => {
                                        const hasPrimary = selectedFontAttr.some(a => a.isPrimary);
                                        const primaryAttrs = hasPrimary
                                            ? selectedFontAttr.filter(a => a.isPrimary)
                                            : selectedFontAttr;
                                        const secondaryAttrs = hasPrimary
                                            ? selectedFontAttr.filter(a => !a.isPrimary)
                                            : [];
                                        const remainingSecondary = secondaryAttrs.filter(
                                            a => !addedSecondaryAttrs.includes(a.name)
                                        );
                                        return (
                                            <>
                                                {primaryAttrs.map(attr => (
                                                    <TextField
                                                        key={attr.name}
                                                        name={attr.name}
                                                        required
                                                        id={attr.name}
                                                        label={attr.name}
                                                        sx={{ mt: 2, width: '48%' }}
                                                        onChange={(e) => setField(attr.name, e.target.value)}
                                                        {...(errors[attr.name] && {
                                                            error: true,
                                                            helperText: errors[attr.name],
                                                        })}
                                                    />
                                                ))}
                                                {addedSecondaryAttrs.map(name => (
                                                    <TextField
                                                        key={name}
                                                        name={name}
                                                        id={name}
                                                        label={name}
                                                        sx={{ mt: 2, width: '48%' }}
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <IconButton size="small" onClick={() => removeSecondaryAttr(name)}>
                                                                        <CloseIcon fontSize="small" />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                        onChange={(e) => setField(name, e.target.value)}
                                                        {...(errors[name] && {
                                                            error: true,
                                                            helperText: errors[name],
                                                        })}
                                                    />
                                                ))}
                                                {remainingSecondary.length > 0 && (
                                                    <FormControl sx={{ mt: 2, width: '48%' }} size="small">
                                                        <InputLabel>Adicionar característica</InputLabel>
                                                        <Select
                                                            key={addedSecondaryAttrs.length}
                                                            label="Adicionar característica"
                                                            defaultValue=""
                                                            onChange={(e) => {
                                                                if (e.target.value) {
                                                                    setAddedSecondaryAttrs(prev => [...prev, e.target.value]);
                                                                }
                                                            }}
                                                        >
                                                            {remainingSecondary.map(attr => (
                                                                <MenuItem key={attr.name} value={attr.name}>{attr.name}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            </>
                                        );
                                    })()}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions />
            </Dialog>

            <Dialog open={openConfirm} onClose={handleCloseConfirm}>
                <DialogTitle>{"Deseja excluir esta obra?"}</DialogTitle>
                <DialogActions>
                    <Button onClick={handleCloseConfirm} color="primary">Cancelar</Button>
                    <Button onClick={handleDeleteArt} color="error" variant="contained">Excluir</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" elevation={6} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};